export interface Dispositivo {
  id: string;
  nome: string | null;
  navegador: string | null;
  navegadorDetectado: string;
  sistemaOp: string | null;
  ip: string | null;
  userAgent: string | null;
  ultimoAcesso: string | null;
  aprovado: boolean;
  esteDispositivo: boolean;
  fingerprint: string;
}

export interface DeviceAccessTimeInfo {
  label: string;
  shortLabel: string;
  diffDays: number;
  diffHours: number;
  diffMonths: number;
  isInativo30Dias: boolean;
  dataExataFormatada: string;
}

/** Ordena dispositivos: dispositivo atual primeiro, seguido pelos demais do mais recente ao mais antigo */
export function sortDevicesByMostRecent(devices: Dispositivo[]): Dispositivo[] {
  return [...devices].sort((a, b) => {
    if (a.esteDispositivo && !b.esteDispositivo) return -1;
    if (!a.esteDispositivo && b.esteDispositivo) return 1;
    const timeA = a.ultimoAcesso ? new Date(a.ultimoAcesso).getTime() : 0;
    const timeB = b.ultimoAcesso ? new Date(b.ultimoAcesso).getTime() : 0;
    return timeB - timeA;
  });
}

/** Detecta dinamicamente as propriedades reais do navegador/hardware do dispositivo atual */
export function detectCurrentDevice(): {
  nome: string;
  navegador: string;
  navegadorDetectado: string;
  sistemaOp: string;
  isMobile: boolean;
  userAgent: string;
} {
  if (typeof window === 'undefined') {
    return {
      nome: 'Meu Computador',
      navegador: 'Chrome',
      navegadorDetectado: 'Chrome 124.0',
      sistemaOp: 'Windows 11',
      isMobile: false,
      userAgent: 'Mozilla/5.0 Desktop',
    };
  }
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isMac = /Macintosh|Mac OS X/.test(ua) && !isIOS;
  const isWindows = /Windows NT/.test(ua);
  const isLinux = /Linux/.test(ua) && !isAndroid;

  let sistemaOp = 'Windows 11';
  if (isIOS) sistemaOp = 'iOS 17.4';
  else if (isAndroid) sistemaOp = 'Android 14';
  else if (isWindows) sistemaOp = 'Windows 11';
  else if (isMac) sistemaOp = 'macOS Sonoma';
  else if (isLinux) sistemaOp = 'Linux';

  let navegador = 'Chrome';
  let navegadorDetectado = 'Chrome';
  if (/Edg\//i.test(ua)) {
    navegador = 'Edge';
    navegadorDetectado = 'Microsoft Edge';
  } else if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) {
    navegador = 'Chrome';
    navegadorDetectado = isIOS || isAndroid ? 'Chrome Mobile' : 'Chrome 124.0';
  } else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) {
    navegador = 'Safari';
    navegadorDetectado = isIOS ? 'Safari Mobile' : 'Safari';
  } else if (/Firefox\//i.test(ua)) {
    navegador = 'Firefox';
    navegadorDetectado = 'Firefox';
  }

  let nome = 'Meu Computador';
  if (isIOS) nome = 'iPhone';
  else if (isAndroid) nome = 'Dispositivo Android';
  else if (isMac) nome = 'Macbook Pro';
  else if (isWindows) nome = 'Meu Computador';

  return {
    nome,
    navegador,
    navegadorDetectado,
    sistemaOp,
    isMobile: isIOS || isAndroid,
    userAgent: ua.slice(0, 100),
  };
}

/** Obtém o ID do usuário atualmente autenticado a partir do auth-storage */
export function getActiveUserId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.state?.user?.id || parsed?.state?.user?.email || null;
    }
  } catch {}
  return null;
}

/** Retorna a chave de armazenamento isolada por usuário */
function getStorageKey(userId?: string | null): string {
  const id = userId || getActiveUserId();
  return id ? `ac_perfil_devices_${id}` : 'ac_perfil_devices_anonymous';
}

/** Dispositivos mock iniciais gerados coerentemente para o usuário */
function getInitialMockDevices(userId?: string | null): Dispositivo[] {
  const now = Date.now();
  const current = detectCurrentDevice();

  const currentDev: Dispositivo = {
    id: `dev-cur-${userId || 'local'}`,
    nome: current.nome,
    navegador: current.navegador,
    navegadorDetectado: current.navegadorDetectado,
    sistemaOp: current.sistemaOp,
    ip: '192.168.1.45',
    userAgent: current.userAgent,
    ultimoAcesso: new Date(now).toISOString(),
    aprovado: true,
    esteDispositivo: true,
    fingerprint: `fp-cur-${userId || 'local'}`,
  };

  const secondaryDev: Dispositivo = current.isMobile
    ? {
        id: `dev-sec-${userId || 'local'}`,
        nome: 'Computador Pessoal',
        navegador: 'Chrome',
        navegadorDetectado: 'Chrome 124.0',
        sistemaOp: 'Windows 11',
        ip: '189.12.98.54',
        userAgent: 'Mozilla/5.0 Windows...',
        ultimoAcesso: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
        aprovado: true,
        esteDispositivo: false,
        fingerprint: `fp-sec-${userId || 'local'}`,
      }
    : {
        id: `dev-sec-${userId || 'local'}`,
        nome: 'Celular',
        navegador: 'Safari',
        navegadorDetectado: 'Safari Mobile',
        sistemaOp: 'iOS 17.4',
        ip: '177.45.2.19',
        userAgent: 'Mozilla/5.0 iPhone...',
        ultimoAcesso: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
        aprovado: true,
        esteDispositivo: false,
        fingerprint: `fp-sec-${userId || 'local'}`,
      };

  return [currentDev, secondaryDev];
}

/** Obtém a lista atual de sessões e dispositivos ativos ordenados do mais recente ao mais antigo */
export function getUserDevices(userId?: string): Dispositivo[] {
  if (typeof window === 'undefined') return sortDevicesByMostRecent(getInitialMockDevices(userId));
  
  // Limpar chave antiga global legada se ainda existir
  try {
    if (localStorage.getItem('ac_perfil_devices')) {
      localStorage.removeItem('ac_perfil_devices');
    }
  } catch {}

  const key = getStorageKey(userId);
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Atualiza as informações do dispositivo atual com os dados do navegador presente
        const current = detectCurrentDevice();
        let hasCurrent = false;
        const normalized = parsed.map((d: Dispositivo) => {
          if (d.esteDispositivo) {
            hasCurrent = true;
            return {
              ...d,
              nome: current.nome,
              navegadorDetectado: current.navegadorDetectado,
              sistemaOp: current.sistemaOp,
              ultimoAcesso: new Date().toISOString(),
            };
          }
          return d;
        });

        if (!hasCurrent && normalized.length > 0) {
          normalized[0].esteDispositivo = true;
          normalized[0].nome = current.nome;
          normalized[0].navegadorDetectado = current.navegadorDetectado;
          normalized[0].sistemaOp = current.sistemaOp;
          normalized[0].ultimoAcesso = new Date().toISOString();
        }

        return sortDevicesByMostRecent(normalized);
      }
    }
  } catch (err) {
    console.warn('[userDevices] Erro ao ler devices do storage:', err);
  }
  const initial = sortDevicesByMostRecent(getInitialMockDevices(userId));
  saveUserDevices(initial, userId);
  return initial;
}

/** Salva e sincroniza a lista de dispositivos globalmente */
export function saveUserDevices(devices: Dispositivo[], userId?: string): void {
  if (typeof window === 'undefined') return;
  const key = getStorageKey(userId);
  try {
    localStorage.setItem(key, JSON.stringify(devices));
    window.dispatchEvent(new CustomEvent('ac:devices:updated', { detail: devices }));
  } catch (err) {
    console.warn('[userDevices] Erro ao salvar devices no storage:', err);
  }
}

/** Revoga/desconecta um dispositivo e notifica a aplicação */
export function revokeUserDevice(id: string, userId?: string): Dispositivo[] {
  const current = getUserDevices(userId);
  const next = current.filter((d) => d.id !== id);
  saveUserDevices(next, userId);
  return next;
}

/** Calcula o tempo relativo de acesso (horas, dias, semanas, meses) e alerta > 30 dias */
export function formatRelativeDeviceAccess(
  ultimoAcesso: string | null | undefined,
  esteDispositivo: boolean = false,
): DeviceAccessTimeInfo {
  if (esteDispositivo) {
    return {
      label: 'Agora',
      shortLabel: 'Agora',
      diffDays: 0,
      diffHours: 0,
      diffMonths: 0,
      isInativo30Dias: false,
      dataExataFormatada: 'Sessão atual',
    };
  }

  if (!ultimoAcesso) {
    return {
      label: 'Data não informada',
      shortLabel: 'Desconhecido',
      diffDays: 0,
      diffHours: 0,
      diffMonths: 0,
      isInativo30Dias: false,
      dataExataFormatada: 'Desconhecido',
    };
  }

  const d = new Date(ultimoAcesso);
  if (isNaN(d.getTime())) {
    return {
      label: 'Data inválida',
      shortLabel: 'Inválido',
      diffDays: 0,
      diffHours: 0,
      diffMonths: 0,
      isInativo30Dias: false,
      dataExataFormatada: 'Inválido',
    };
  }

  const dataExataFormatada = d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const now = Date.now();
  const diffMs = Math.max(0, now - d.getTime());
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  const isInativo30Dias = diffDays >= 30;

  let label = '';
  let shortLabel = '';

  if (diffMinutes < 1) {
    label = 'Há poucos segundos';
    shortLabel = 'agora';
  } else if (diffMinutes < 60) {
    label = `Há ${diffMinutes} ${diffMinutes === 1 ? 'minuto' : 'minutos'}`;
    shortLabel = `há ${diffMinutes}m`;
  } else if (diffHours < 24) {
    label = `Há ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
    shortLabel = `há ${diffHours}h`;
  } else if (diffDays === 1) {
    label = `Há 1 dia (${dataExataFormatada})`;
    shortLabel = 'ontem';
  } else if (diffDays < 7) {
    label = `Há ${diffDays} dias (${dataExataFormatada})`;
    shortLabel = `há ${diffDays}d`;
  } else if (diffWeeks === 1) {
    label = `Há 1 semana (${dataExataFormatada})`;
    shortLabel = 'há 1 sem';
  } else if (diffWeeks < 4) {
    label = `Há ${diffWeeks} semanas (${dataExataFormatada})`;
    shortLabel = `há ${diffWeeks} sem`;
  } else if (diffMonths === 1) {
    label = `Há 1 mês (${dataExataFormatada})`;
    shortLabel = 'há 1 mês';
  } else {
    label = `Há ${diffMonths} meses (${dataExataFormatada})`;
    shortLabel = `há ${diffMonths} meses`;
  }

  return {
    label,
    shortLabel,
    diffDays,
    diffHours,
    diffMonths,
    isInativo30Dias,
    dataExataFormatada,
  };
}

/** Filtra os dispositivos inativos há mais de 30 dias que não sejam o atual */
export function getInactiveDevices(devices?: Dispositivo[], userId?: string): Dispositivo[] {
  const list = devices || getUserDevices(userId);
  return list.filter((dev) => {
    if (dev.esteDispositivo) return false;
    const info = formatRelativeDeviceAccess(dev.ultimoAcesso, false);
    return info.isInativo30Dias;
  });
}
